/**
 * Data Retention Policy Service
 * Implements HIPAA-compliant data retention and automatic cleanup
 */

import { auditLogger } from './auditLogger';

export interface RetentionPolicy {
  /** Data category name */
  category: string;

  /** Retention period in milliseconds */
  retentionPeriod: number;

  /** Whether to encrypt this category */
  encrypted: boolean;

  /** Auto-delete after retention period */
  autoDelete: boolean;
}

export interface RetentionConfig {
  policies: RetentionPolicy[];
  cleanupInterval: number; // milliseconds
  enabled: boolean;
}

export interface StoredData {
  key: string;
  category: string;
  data: unknown;
  createdAt: string;
  expiresAt: string;
  encrypted: boolean;
}

class DataRetentionService {
  private config: RetentionConfig;
  private cleanupTimer: NodeJS.Timeout | null = null;

  // Default HIPAA-compliant retention policies
  private defaultPolicies: RetentionPolicy[] = [
    {
      category: 'appointment_data',
      retentionPeriod: 6 * 365 * 24 * 60 * 60 * 1000, // 6 years (HIPAA requirement)
      encrypted: true,
      autoDelete: false, // Don't auto-delete, require manual archive
    },
    {
      category: 'patient_info',
      retentionPeriod: 6 * 365 * 24 * 60 * 60 * 1000, // 6 years
      encrypted: true,
      autoDelete: false,
    },
    {
      category: 'session_data',
      retentionPeriod: 24 * 60 * 60 * 1000, // 24 hours
      encrypted: false,
      autoDelete: true,
    },
    {
      category: 'form_drafts',
      retentionPeriod: 7 * 24 * 60 * 60 * 1000, // 7 days
      encrypted: false,
      autoDelete: true,
    },
    {
      category: 'analytics_data',
      retentionPeriod: 90 * 24 * 60 * 60 * 1000, // 90 days
      encrypted: false,
      autoDelete: true,
    },
    {
      category: 'audit_logs',
      retentionPeriod: 6 * 365 * 24 * 60 * 60 * 1000, // 6 years (HIPAA requirement)
      encrypted: false,
      autoDelete: false,
    },
  ];

  constructor(config?: Partial<RetentionConfig>) {
    this.config = {
      policies: this.defaultPolicies,
      cleanupInterval: 24 * 60 * 60 * 1000, // 24 hours
      enabled: true,
      ...config,
    };

    if (this.config.enabled) {
      this.startCleanupTimer();
    }
  }

  /**
   * Store data with automatic expiration based on category
   */
  async store(
    key: string,
    category: string,
    data: unknown
  ): Promise<void> {
    const policy = this.getPolicyForCategory(category);

    if (!policy) {
      throw new Error(`No retention policy found for category: ${category}`);
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + policy.retentionPeriod);

    const storedData: StoredData = {
      key,
      category,
      data,
      createdAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      encrypted: policy.encrypted,
    };

    // Store in sessionStorage (in production, this would go to a backend)
    const storageKey = `ww_retention_${category}_${key}`;
    sessionStorage.setItem(storageKey, JSON.stringify(storedData));

    await auditLogger.logSecurityEvent('DATA_STORED', 'success', {
      category,
      key,
      expiresAt: expiresAt.toISOString(),
    });
  }

  /**
   * Retrieve stored data if not expired
   */
  async retrieve(key: string, category: string): Promise<unknown | null> {
    const storageKey = `ww_retention_${category}_${key}`;
    const storedJson = sessionStorage.getItem(storageKey);

    if (!storedJson) {
      return null;
    }

    try {
      const stored: StoredData = JSON.parse(storedJson);

      // Check if expired
      if (this.isExpired(stored)) {
        await this.delete(key, category);
        return null;
      }

      return stored.data;
    } catch (error) {
      console.error('[RETENTION] Failed to retrieve data:', error);
      return null;
    }
  }

  /**
   * Delete data
   */
  async delete(key: string, category: string): Promise<void> {
    const storageKey = `ww_retention_${category}_${key}`;
    sessionStorage.removeItem(storageKey);

    await auditLogger.logSecurityEvent('DATA_DELETED', 'success', {
      category,
      key,
    });
  }

  /**
   * Clean up all expired data
   */
  async cleanup(): Promise<number> {
    if (!this.config.enabled) {
      return 0;
    }

    let deletedCount = 0;

    // Iterate through all storage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (!key || !key.startsWith('ww_retention_')) {
        continue;
      }

      const storedJson = sessionStorage.getItem(key);
      if (!storedJson) {
        continue;
      }

      try {
        const stored: StoredData = JSON.parse(storedJson);
        const policy = this.getPolicyForCategory(stored.category);

        // Delete if expired and auto-delete is enabled
        if (this.isExpired(stored) && policy?.autoDelete) {
          sessionStorage.removeItem(key);
          deletedCount++;
        }
      } catch (error) {
        console.error('[RETENTION] Error processing item:', error);
      }
    }

    if (deletedCount > 0) {
      await auditLogger.logSecurityEvent('DATA_CLEANUP', 'success', {
        deletedCount,
      });
    }

    return deletedCount;
  }

  /**
   * Get all data for a specific category
   */
  async getAllByCategory(category: string): Promise<StoredData[]> {
    const results: StoredData[] = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (!key || !key.startsWith(`ww_retention_${category}_`)) {
        continue;
      }

      const storedJson = sessionStorage.getItem(key);
      if (!storedJson) {
        continue;
      }

      try {
        const stored: StoredData = JSON.parse(storedJson);

        if (!this.isExpired(stored)) {
          results.push(stored);
        }
      } catch (error) {
        console.error('[RETENTION] Error parsing item:', error);
      }
    }

    return results;
  }

  /**
   * Archive data before deletion (send to backend for long-term storage)
   */
  async archive(key: string, category: string): Promise<void> {
    const data = await this.retrieve(key, category);

    if (!data) {
      throw new Error('Data not found or already expired');
    }

    // In production, send to backend API for archival
    await auditLogger.logSecurityEvent('DATA_ARCHIVED', 'success', {
      category,
      key,
    });

    // After successful archive, delete from client storage
    await this.delete(key, category);
  }

  /**
   * Get retention policy for a category
   */
  getPolicyForCategory(category: string): RetentionPolicy | undefined {
    return this.config.policies.find(p => p.category === category);
  }

  /**
   * Add or update a retention policy
   */
  setPolicy(policy: RetentionPolicy): void {
    const existingIndex = this.config.policies.findIndex(
      p => p.category === policy.category
    );

    if (existingIndex >= 0) {
      this.config.policies[existingIndex] = policy;
    } else {
      this.config.policies.push(policy);
    }
  }

  /**
   * Check if data is expired
   */
  private isExpired(stored: StoredData): boolean {
    const now = new Date();
    const expiresAt = new Date(stored.expiresAt);
    return now > expiresAt;
  }

  /**
   * Start automatic cleanup timer
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup().catch(console.error);
    }, this.config.cleanupInterval);

    // Run cleanup immediately on startup
    this.cleanup().catch(console.error);
  }

  /**
   * Stop cleanup timer
   */
  stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<RetentionConfig>): void {
    this.config = { ...this.config, ...config };

    if (config.cleanupInterval) {
      this.startCleanupTimer();
    }
  }

  /**
   * Destroy the service
   */
  async destroy(): Promise<void> {
    this.stopCleanupTimer();
    await this.cleanup();
  }
}

// Singleton instance
let dataRetentionServiceInstance: DataRetentionService | null = null;

export function getDataRetentionService(
  config?: Partial<RetentionConfig>
): DataRetentionService {
  if (!dataRetentionServiceInstance) {
    dataRetentionServiceInstance = new DataRetentionService(config);
  }
  return dataRetentionServiceInstance;
}

export const dataRetentionService = getDataRetentionService();
